import { useSelector } from 'react-redux';

import { PaymentStatusEnum } from '../../lib/enums';
import { PlanPhaseTypeEnum } from '../../lib/models/crm/types';
import { RootState } from '../../lib/store';
import {
  AccountLifecycleEnum,
  getAccountLifecycle,
  hasActivePaidPlan,
  isDemoAccountSettingUp,
  isFundedPhase,
} from '../../lib/store/account-flow/lifecycle';
import {
  hasCurrentAccountBuyingPower,
  isCurrentAccountAwaitingApproval,
} from '../../lib/store/account-flow/selectors';
import {
  getCurrentAccount,
  getCurrentAccountTradingPlan,
  hasAnyAccountDemoPlan,
  hasAnyAccountPaidPlan,
  hasNonClosedPaidPlan,
} from '../../lib/store/crm/helpers';
import { getOldestAdjustmentOfCurrentAccount } from '../../lib/store/selectors';
import { isDemoAccountActivating, isDemoAccountReference } from '../../lib/util/account-helpers';
import { isPlanPurchaseCallbackInProgress } from '../../lib/util/url-helpers';



type AccountLifecycle = {
  isDemoSetup: boolean;
  lifecycle: AccountLifecycleEnum;
  phase?: PlanPhaseTypeEnum;
};

/**
 * The single place the account markers are read from, so the notifications all agree on which stage
 * the account is at and which account it is.
 */
const useAccountLifecycle = (): AccountLifecycle => {
  const currentAccount = useSelector(getCurrentAccount);
  const firstAdjustment = useSelector(getOldestAdjustmentOfCurrentAccount);
  const isUseDemoAccount = useSelector((state: RootState) => state.crm.isUseDemoAccount);
  // The account being created is not in the store yet, so its reference is what tells demo apart.
  const pendingTradingAccount = useSelector(
    (state: RootState) => state.crm.pendingTradingAccountReference,
  );
  const hasDemoPlan = useSelector((state: RootState) => hasAnyAccountDemoPlan(state.crm));
  const hasPaidPlan = useSelector((state: RootState) => hasAnyAccountPaidPlan(state.crm));
  const hasNonClosedPlan = useSelector((state: RootState) => hasNonClosedPaidPlan(state.crm));
  const tradingPlan = useSelector((state: RootState) => getCurrentAccountTradingPlan(state.crm));

  const isActivatingDemo = isDemoAccountActivating();
  const isPendingAccountDemo = isDemoAccountReference(pendingTradingAccount);

  const isAwaitingApproval = useSelector(isCurrentAccountAwaitingApproval);
  const hasBuyingPower = useSelector(hasCurrentAccountBuyingPower);

  const lifecycle = getAccountLifecycle({
    currentAccount,
    hasActivePaidPlan: hasActivePaidPlan({ hasNonClosedPlan, hasPaidPlan, isUseDemoAccount }),
    isAwaitingApproval,
    hasPendingTradingAccount: !!pendingTradingAccount,
    isDemoAccountActivating: isActivatingDemo,
    isFirstAdjustmentCompleted: firstAdjustment?.payment_status === PaymentStatusEnum.Completed,
    hasBuyingPower,
    isFundedPhase: isFundedPhase(tradingPlan?.phase),
    isPlanCallback: isPlanPurchaseCallbackInProgress(),
    isUseDemoAccount,
  });

  return {
    isDemoSetup: isDemoAccountSettingUp({
      hasDemoPlan,
      isDemoAccountActivating: isActivatingDemo,
      isPendingAccountDemo,
      isUseDemoAccount,
    }),
    lifecycle,
    phase: tradingPlan?.phase,
  };
};

export default useAccountLifecycle;
